import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './VolunteerRequirements.module.css';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupRange } from 'utils/groupLabel';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Users,
  Calendar,
  Shield,
  Flag,
} from 'lucide-react';
import CustomButton from 'components/common/CustomButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { TRACK_EVENTS_CODE } from 'constants/eventTypes';
import {
  getTrackEventCodes,
  mappingMatchesEvents,
} from 'utils/volunteerProgramUtils';

const VolunteerRequirements = ({
  volunteerRoles = [],
  programGroups = [], // Groups currently in the program
  clubEventTypes = [], // Event types from API for display names
  requirements,
  onRequirementChange,
  onSave,
}) => {
  // A role's groups read "Age Groups: U10-U11", or "Year Groups: Y5-Y6" for a club that groups by year level.
  const { groupingBasis, words } = useGroupLabels();
  const [expandedRoles, setExpandedRoles] = useState({});
  // The three sections start closed, so the page opens short; each header shows how many roles it
  // holds (Doug, 2026-10-26).
  const [expandedSections, setExpandedSections] = useState({
    general: false,
    eventOfficial: false,
    athleteGroups: false,
  });
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isLoading] = useState(false);
  // Light confirm shown when removing a role that has signups: the removal is
  // allowed, and the signups are cancelled at save time behind the
  // authoritative review dialog (design doc §16) — never silently.
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState({
    isOpen: false,
    roleId: '',
    roleName: '',
    group: null,
    numberCommitted: 0,
  });

  // Include-per-meet editor (volunteer-role-deletion-design.md §13): cards show
  // only the roles included in this meet; everything else lives behind a
  // transient "+ Add role…" dropdown per section/group.
  // addingIn = the section/group key whose add-dropdown is open (or null).
  const [addingIn, setAddingIn] = useState(null);
  const [addSearchText, setAddSearchText] = useState('');

  // Helper to get event type display name from eventCode
  const getEventTypeName = useCallback(
    (eventCode) => {
      if (eventCode === TRACK_EVENTS_CODE) return 'Track Events';
      const eventType = clubEventTypes.find((et) => et.eventCode === eventCode);
      return eventType?.displayName || `Event ${eventCode}`;
    },
    [clubEventTypes]
  );

  // Event codes the club classifies as track events (category Track or
  // Training) — what a role's Track Events mapping expands to.
  const trackEventCodes = useMemo(
    () => getTrackEventCodes(clubEventTypes),
    [clubEventTypes]
  );

  // Map program groups with their events
  const activeGroups = useMemo(() => {
    return programGroups.map((pg) => ({
      ...pg,
      isActive: true,
      events: pg.events || [],
    }));
  }, [programGroups]);

  // NOTE: the "Additional Roles Not in Program" (orphaned roles) panel was
  // REMOVED 2026-07-13 (program-edit-impact-design.html §10). The Program Edit
  // Impact Review enforcement makes new orphans impossible (program save
  // resolves dependents atomically via the 409 + Impact Review dialog, the
  // requirements PUT rejects creating non-resolving requirements, copy-from-
  // meet filters them, and the signup endpoints refuse them), and the only
  // legacy orphans in production sit on past meets nobody edits. Legacy
  // orphaned requirement rows remain in the data as inert history: they carry
  // their IDs through the requirements PUT untouched, never render here, and
  // can be inspected with docs/program-edit-orphan-cleanup.sql (Part 2).

  // Organize roles by category
  const { generalRoles, eventOfficialRoles, ageGroupSpecificRoles } =
    useMemo(() => {
      const general = [];
      const eventOfficial = [];
      const ageSpecific = [];

      volunteerRoles.forEach((role) => {
        if (role.roleCategory === 'General') {
          general.push(role);
        } else if (role.roleCategory === 'EventSpecific') {
          eventOfficial.push(role);
        } else if (role.roleCategory === 'AgeGroupSpecific') {
          ageSpecific.push(role);
        }
      });

      return {
        generalRoles: general,
        eventOfficialRoles: eventOfficial,
        ageGroupSpecificRoles: ageSpecific,
      };
    }, [volunteerRoles]);

  // Helper function to check if a role applies to a group based on age range
  const doesRoleApplyToGroup = useCallback((role, group) => {
    if (!role.ageGroupCode) return false;
    if (!group.ageRange) return false;

    const roleAgeCode = role.ageGroupCode;
    const groupAgeRange = group.ageRange;

    // Direct match
    if (roleAgeCode === groupAgeRange) return true;

    // Extract age numbers from strings
    const extractAgeNumber = (ageStr) => {
      return parseInt(ageStr.replace(/[^\d]/g, ''));
    };

    // Get role age range
    let roleStartNum, roleEndNum;
    if (roleAgeCode.includes('-')) {
      const [startAge, endAge] = roleAgeCode.split('-');
      roleStartNum = extractAgeNumber(startAge);
      roleEndNum = extractAgeNumber(endAge);
    } else {
      roleStartNum = roleEndNum = extractAgeNumber(roleAgeCode);
    }

    // Get group age range
    let groupStartNum, groupEndNum;
    if (groupAgeRange.includes('-')) {
      const [startAge, endAge] = groupAgeRange.split('-');
      groupStartNum = extractAgeNumber(startAge);
      groupEndNum = extractAgeNumber(endAge);
    } else {
      groupStartNum = groupEndNum = extractAgeNumber(groupAgeRange);
    }

    // Check if ranges overlap
    // A role applies if there's any overlap between the role's age range and the group's age range
    if (
      !isNaN(roleStartNum) &&
      !isNaN(roleEndNum) &&
      !isNaN(groupStartNum) &&
      !isNaN(groupEndNum)
    ) {
      return roleStartNum <= groupEndNum && roleEndNum >= groupStartNum;
    }

    return false;
  }, []);

  // Check if a role's event types match any of the group's events
  // (Track Events roles match any track/training event in the group)
  const doesRoleMatchGroupEvents = useCallback(
    (role, group) => {
      // If role has no event type mapping, it applies to all events
      if (!role.eventTypeMapping || role.eventTypeMapping.length === 0) {
        return true;
      }

      // Get the event types from the group's events
      const groupEventTypes = (group.events || [])
        .map((event) => event.eventType)
        .filter((eventType) => eventType != null);

      return mappingMatchesEvents(
        role.eventTypeMapping,
        groupEventTypes,
        trackEventCodes
      );
    },
    [trackEventCodes]
  );

  // Get age-specific roles for a group
  const getRolesForGroup = useCallback(
    (group) => {
      return ageGroupSpecificRoles.filter(
        (role) =>
          doesRoleApplyToGroup(role, group) &&
          doesRoleMatchGroupEvents(role, group)
      );
    },
    [ageGroupSpecificRoles, doesRoleApplyToGroup, doesRoleMatchGroupEvents]
  );

  // All event codes anywhere in the program — Event Official roles only
  // show when their mapped event (or, for Track Events roles, any
  // track/training event) is in the program.
  const programEventCodes = useMemo(
    () =>
      programGroups.flatMap((group) =>
        (group.events || [])
          .map((event) => event.eventType)
          .filter((eventType) => eventType != null)
      ),
    [programGroups]
  );

  const visibleEventOfficialRoles = useMemo(
    () =>
      eventOfficialRoles.filter(
        (role) =>
          !role.eventTypeMapping ||
          role.eventTypeMapping.length === 0 ||
          mappingMatchesEvents(
            role.eventTypeMapping,
            programEventCodes,
            trackEventCodes
          )
      ),
    [eventOfficialRoles, programEventCodes, trackEventCodes]
  );

  // Generate requirement key - for general/event roles, no group suffix
  const getRequirementKey = (roleId, group = null) => {
    if (!group) {
      return roleId;
    }
    return `${roleId}_${group.displayName}_${group.groupSource || group.groupType}`;
  };

  const getGroupKey = (group) =>
    `${group.displayName}_${group.groupSource || group.groupType}`;

  const isIncluded = (role, group = null) =>
    Boolean(requirements[getRequirementKey(role.volunteerRoleID, group)]?.include);

  // Toggle expanded state
  const toggleExpanded = (roleId) => {
    setExpandedRoles((prev) => ({
      ...prev,
      [roleId]: !prev[roleId],
    }));
  };

  const toggleSectionExpanded = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleExpandedGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Apply an include/exclude change to the requirements map (no guards).
  const applyIncludeChange = (roleId, included, group = null) => {
    const role = volunteerRoles.find((r) => r.volunteerRoleID === roleId);
    const requirementKey = getRequirementKey(roleId, group);
    const existingReq = requirements[requirementKey];

    const newRequirement = {
      meetVolunteerRequirementID: existingReq?.meetVolunteerRequirementID,
      volunteerRoleID: roleId,
      include: included,
      numberRequired: included
        ? existingReq?.numberRequired || role?.numberRequiredDefault || 1
        : 0,
      // Group-specific fields (null for general/event roles)
      programGroupName: group?.displayName || null,
      groupType: group ? group.groupSource || group.groupType : null,
      ageGroupCode: group?.ageRange || null,
      manuallySet: true,
    };
    onRequirementChange(roleId, newRequirement, requirementKey);
  };

  // Handle include/exclude toggle. Removing a role that has signups gets a
  // light confirm here for local feedback; the authoritative, named-signups
  // confirmation happens at save time via the requirements PUT's 409 review.
  const handleIncludeChange = (roleId, included, group = null) => {
    const role = volunteerRoles.find((r) => r.volunteerRoleID === roleId);
    const requirementKey = getRequirementKey(roleId, group);
    const existingReq = requirements[requirementKey];

    const numberCommitted = existingReq?.numberCommitted || 0;
    if (!included && numberCommitted > 0) {
      setRemoveConfirmDialog({
        isOpen: true,
        roleId,
        roleName: role?.roleName || 'This role',
        group,
        numberCommitted,
      });
      return;
    }

    applyIncludeChange(roleId, included, group);
  };

  const closeRemoveConfirm = () =>
    setRemoveConfirmDialog({
      isOpen: false,
      roleId: '',
      roleName: '',
      group: null,
      numberCommitted: 0,
    });

  const confirmRemoveRole = () => {
    applyIncludeChange(removeConfirmDialog.roleId, false, removeConfirmDialog.group);
    closeRemoveConfirm();
  };

  // Handle number change
  const handleNumberChange = (roleId, number, group = null) => {
    const requirementKey = getRequirementKey(roleId, group);
    const existingReq = requirements[requirementKey];

    const newRequirement = {
      ...existingReq,
      volunteerRoleID: roleId,
      numberRequired: Math.max(0, parseInt(number) || 0),
      manuallySet: true,
    };
    onRequirementChange(roleId, newRequirement, requirementKey);
  };

  const toggleAddDropdown = (key) => {
    setAddSearchText('');
    setAddingIn((prev) => (prev === key ? null : key));
  };

  // Add a role from the add-dropdown. Roles are added to one section/group at
  // a time — no fan-out to other groups (role eligibility rules like
  // gender-specific roles make bulk-adding unsafe).
  const handleAddRole = (role, group = null) => {
    handleIncludeChange(role.volunteerRoleID, true, group);
    setAddingIn(null);
    setAddSearchText('');
  };

  // Render an INCLUDED role card. Only included roles render as cards; adding
  // more happens through the add-role row (design doc §13: cards show
  // decisions, not options).
  const renderRoleItem = (role, group = null) => {
    const requirementKey = getRequirementKey(role.volunteerRoleID, group);
    const requirement = requirements[requirementKey] || {};
    const isExpanded = expandedRoles[role.volunteerRoleID];
    const numberCommitted = requirement.numberCommitted || 0;
    const hasSignups = numberCommitted > 0;

    return (
      <div
        key={requirementKey}
        className={`${styles.roleCard} ${styles.includedAgeGroup}`}
      >
        <div className={styles.roleHeader}>
          <div className={styles.roleInfo}>
            <CustomButton
              onClick={() => toggleExpanded(role.volunteerRoleID)}
              style={{
                color: '#9ca3af',
                backgroundColor: 'transparent',
                padding: '0',
                flexShrink: 0,
                margin: '0px',
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </CustomButton>
            <div className={styles.roleTitleWrapper}>
              <h4 className={styles.roleTitle}>{role.roleName}</h4>
              <div className={styles.hoursBadge}>
                ⏱️ {role.hoursEarned.toFixed(1)} hour
                {role.hoursEarned === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className={styles.roleControls}>
            {/* Informational only — no navigation out of the editor while
                editing; removals with signups are handled by the save-time
                review (§16), so Manage Signups is not needed from here. */}
            <span
              className={`${styles.signupsBadge} ${hasSignups ? styles.hasSignups : ''}`}
              title={hasSignups ? 'Volunteers have signed up for this role' : 'No signups yet'}
            >
              {numberCommitted} {numberCommitted === 1 ? 'Signup' : 'Signups'}
            </span>

            <div className={styles.numberControl}>
              <span>Need:</span>
              <div className={styles.selectWrapper}>
                <select
                  value={
                    requirement.numberRequired ||
                    role.numberRequiredDefault ||
                    1
                  }
                  onChange={(e) =>
                    handleNumberChange(
                      role.volunteerRoleID,
                      e.target.value,
                      group
                    )
                  }
                  className={styles.numberSelect}
                >
                  {[...Array(20)].map((_, i) => {
                    const value = i + 1;
                    // Only show options >= numberCommitted
                    if (value < numberCommitted) return null;
                    return (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className={styles.selectIcon} />
              </div>
            </div>

            <button
              type="button"
              className={styles.removeRoleButton}
              title="Remove this role from the meet"
              onClick={() =>
                handleIncludeChange(role.volunteerRoleID, false, group)
              }
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className={styles.roleDetails}>
            {role.description && (
              <p className={styles.description}>{role.description}</p>
            )}
            {role.eventTypeMapping && role.eventTypeMapping.length > 0 && (
              <div className={styles.relevantEvents}>
                <strong>Relevant Events: </strong>
                {role.eventTypeMapping
                  .map((eventCode) => getEventTypeName(eventCode))
                  .join(', ')}
              </div>
            )}
            {role.ageGroupCode && (
              <div className={styles.ageGroupInfo}>
                <strong>{words.groups}: </strong>
                {formatGroupRange(groupingBasis, role.ageGroupCode)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // The "+ Add role…" row with its transient dropdown of eligible-but-not-
  // included roles (design doc §13). Renders nothing when every eligible role
  // is already included.
  const renderAddRoleRow = (availableRoles, group, addKey) => {
    if (availableRoles.length === 0) return null;

    const isOpen = addingIn === addKey;
    const search = addSearchText.trim().toLowerCase();
    const filteredRoles = search
      ? availableRoles.filter((role) =>
          role.roleName.toLowerCase().includes(search)
        )
      : availableRoles;

    return (
      <div className={styles.addRoleContainer}>
        <button
          type="button"
          className={styles.addRoleRow}
          onClick={() => toggleAddDropdown(addKey)}
        >
          <Plus size={16} />
          <span>
            Add role… ({availableRoles.length} available)
          </span>
        </button>

        {isOpen && (
          <div className={styles.addRoleDropdown}>
            {availableRoles.length > 8 && (
              <input
                type="text"
                className={styles.addRoleSearch}
                placeholder="Search roles..."
                value={addSearchText}
                onChange={(e) => setAddSearchText(e.target.value)}
                autoFocus
              />
            )}
            {filteredRoles.length === 0 ? (
              <div className={styles.addRoleEmpty}>No matching roles</div>
            ) : (
              filteredRoles.map((role) => (
                <button
                  key={role.volunteerRoleID}
                  type="button"
                  className={styles.addRoleOption}
                  onClick={() => handleAddRole(role, group)}
                >
                  <span className={styles.addRoleOptionName}>
                    {role.roleName}
                  </span>
                  <span className={styles.addRoleOptionHours}>
                    ⏱️ {role.hoursEarned.toFixed(1)}h
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Render group section for athlete groups
  const renderGroupSection = (group) => {
    const groupKey = getGroupKey(group);
    const isExpanded =
      expandedGroups[groupKey] !== undefined ? expandedGroups[groupKey] : false; // Default to collapsed for all groups
    const roles = getRolesForGroup(group);
    const includedRoles = roles.filter((role) => isIncluded(role, group));
    const availableRoles = roles.filter((role) => !isIncluded(role, group));

    // Header summary: included roles and how staffed they are
    const totalRequired = includedRoles.reduce((sum, role) => {
      const req = requirements[getRequirementKey(role.volunteerRoleID, group)];
      return sum + (req?.numberRequired || 0);
    }, 0);
    const totalCommitted = includedRoles.reduce((sum, role) => {
      const req = requirements[getRequirementKey(role.volunteerRoleID, group)];
      return sum + (req?.numberCommitted || 0);
    }, 0);

    // Group label with type indicator
    const groupLabel =
      group.groupSource === 'AthleteGroup'
        ? `${group.displayName} `
        : group.displayName;

    return (
      <div key={groupKey} className={styles.programGroupSection}>
        <div
          className={styles.programGroupHeader}
          onClick={() => toggleExpandedGroup(groupKey)}
        >
          <div className={styles.programGroupInfo}>
            {isExpanded ? (
              <ChevronDown size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
            <h4 className={styles.programGroupTitle}>{groupLabel}</h4>
            {group.groupSource === 'AthleteGroup' && (
              <Users size={16} className={styles.groupIcon} />
            )}
            {group.memberCount && (
              <span className={styles.memberCount}>
                {group.memberCount} athletes
              </span>
            )}
            <span
              className={
                includedRoles.length > 0
                  ? styles.groupIncludedBadge
                  : styles.roleCount
              }
            >
              {includedRoles.length > 0
                ? `${includedRoles.length} ${includedRoles.length === 1 ? 'role' : 'roles'} · ${totalCommitted}/${totalRequired} filled`
                : 'No roles yet'}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className={styles.programGroupRoles}>
            {includedRoles.map((role) => renderRoleItem(role, group))}
            {includedRoles.length === 0 && (
              <div className={styles.noRoles}>
                {availableRoles.length > 0
                  ? 'No volunteer roles yet — add one below.'
                  : 'No specific roles for this group'}
              </div>
            )}
            {renderAddRoleRow(availableRoles, group, groupKey)}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <h3 className={styles.title}>Volunteer Requirements</h3>
        <div className={styles.loadingPlaceholder}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.loadingItem} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Volunteer Requirements</h3>
        <p className={styles.subtitle}>
          The volunteer roles included in this meet are shown below — use Add
          role to include more. General and Event Official roles apply to the
          entire meet, while Athlete Group roles are specific to {words.lower.groups} or
          athlete groups.
        </p>
      </div>

      <div className={styles.rolesList}>
        {/* General Roles Section */}
        {generalRoles.length > 0 &&
          (() => {
            const includedRoles = generalRoles.filter((role) =>
              isIncluded(role)
            );
            const availableRoles = generalRoles.filter(
              (role) => !isIncluded(role)
            );
            return (
              <div className={styles.topLevelSection}>
                <div
                  className={styles.topLevelSectionHeader}
                  onClick={() => toggleSectionExpanded('general')}
                >
                  <div className={styles.sectionHeaderContent}>
                    {expandedSections.general ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                    <Shield size={18} className={styles.sectionIcon} />
                    <h4 className={styles.mainSectionTitle}>General Roles</h4>
                    <span className={styles.sectionDescription}>
                      Roles that apply to the entire meet
                    </span>
                    {includedRoles.length > 0 && (
                      <span className={styles.includedBadge}>
                        {includedRoles.length}{' '}
                        {includedRoles.length === 1 ? 'role' : 'roles'}
                      </span>
                    )}
                  </div>
                </div>

                {expandedSections.general && (
                  <div className={styles.topLevelRolesList}>
                    {includedRoles.map((role) => renderRoleItem(role))}
                    {includedRoles.length === 0 && (
                      <div className={styles.noRoles}>
                        No general roles yet — add one below.
                      </div>
                    )}
                    {renderAddRoleRow(availableRoles, null, 'general')}
                  </div>
                )}
              </div>
            );
          })()}

        {/* Event Officials Section */}
        {visibleEventOfficialRoles.length > 0 &&
          (() => {
            const includedRoles = visibleEventOfficialRoles.filter((role) =>
              isIncluded(role)
            );
            const availableRoles = visibleEventOfficialRoles.filter(
              (role) => !isIncluded(role)
            );
            return (
              <div className={styles.topLevelSection}>
                <div
                  className={styles.topLevelSectionHeader}
                  onClick={() => toggleSectionExpanded('eventOfficial')}
                >
                  <div className={styles.sectionHeaderContent}>
                    {expandedSections.eventOfficial ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                    <Flag size={18} className={styles.sectionIcon} />
                    <h4 className={styles.mainSectionTitle}>Event Officials</h4>
                    <span className={styles.sectionDescription}>
                      Officials for specific event types
                    </span>
                    {includedRoles.length > 0 && (
                      <span className={styles.includedBadge}>
                        {includedRoles.length}{' '}
                        {includedRoles.length === 1 ? 'role' : 'roles'}
                      </span>
                    )}
                  </div>
                </div>

                {expandedSections.eventOfficial && (
                  <div className={styles.topLevelRolesList}>
                    {includedRoles.map((role) => renderRoleItem(role))}
                    {includedRoles.length === 0 && (
                      <div className={styles.noRoles}>
                        No event official roles yet — add one below.
                      </div>
                    )}
                    {renderAddRoleRow(availableRoles, null, 'eventOfficial')}
                  </div>
                )}
              </div>
            );
          })()}

        {/* Athlete Groups Section */}
        {activeGroups.length > 0 &&
          (() => {
            // Count all included age-specific roles across all groups
            let totalIncluded = 0;
            activeGroups.forEach((group) => {
              getRolesForGroup(group).forEach((role) => {
                if (isIncluded(role, group)) {
                  totalIncluded++;
                }
              });
            });

            return (
              <div className={styles.topLevelSection}>
                <div
                  className={styles.topLevelSectionHeader}
                  onClick={() => toggleSectionExpanded('athleteGroups')}
                >
                  <div className={styles.sectionHeaderContent}>
                    {expandedSections.athleteGroups ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                    <Users size={18} className={styles.sectionIcon} />
                    {/* Headed in the club's word, as the program designer's column is (Doug, 2026-10-14) */}
                    <h4 className={styles.mainSectionTitle}>{words.groups}</h4>
                    <span className={styles.sectionDescription}>
                      Roles specific to {words.lower.groups}
                    </span>
                    {totalIncluded > 0 && (
                      <span className={styles.includedBadge}>
                        {totalIncluded} {totalIncluded === 1 ? 'role' : 'roles'}
                      </span>
                    )}
                  </div>
                </div>

                {expandedSections.athleteGroups && (
                  <div className={styles.athleteGroupsContent}>
                    <div className={styles.activeGroupsSection}>
                      <div className={styles.subSectionHeader}>
                        <Calendar size={16} />
                        <span className={styles.subSectionTitle}>
                          Groups in Program
                        </span>
                      </div>
                      {activeGroups.map((group) => renderGroupSection(group))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      </div>

      {onSave && (
        <div className={styles.footer}>
          <CustomButton onClick={onSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Requirements'}
          </CustomButton>
        </div>
      )}

      {/* Light confirm when removing a role that has signups; the full,
          named-signups review happens at save time (design doc §16) */}
      <CustomNewConfirm
        isOpen={removeConfirmDialog.isOpen}
        onClose={closeRemoveConfirm}
        title="Remove Role From Meet?"
        description={`"${removeConfirmDialog.roleName}" has ${removeConfirmDialog.numberCommitted} volunteer${removeConfirmDialog.numberCommitted !== 1 ? 's' : ''} signed up. Their signups will be cancelled when you save — you'll see the full list and confirm before anything changes.`}
        confirmButtonText="Remove Role"
        confirmButtonVariant="danger"
        onConfirm={confirmRemoveRole}
        cancelButtonText="Keep Role"
      />
    </div>
  );
};

VolunteerRequirements.propTypes = {
  volunteerRoles: PropTypes.arrayOf(
    PropTypes.shape({
      volunteerRoleID: PropTypes.string.isRequired,
      roleName: PropTypes.string.isRequired,
      roleCategory: PropTypes.oneOf([
        'EventSpecific',
        'General',
        'AgeGroupSpecific',
      ]).isRequired,
      hoursEarned: PropTypes.number.isRequired,
      numberRequiredDefault: PropTypes.number,
      eventTypeMapping: PropTypes.arrayOf(PropTypes.number),
      description: PropTypes.string,
      isAgeGroupSpecific: PropTypes.bool,
      ageGroupCode: PropTypes.string,
    })
  ),
  programGroups: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      groupSource: PropTypes.oneOf(['AgeGroup', 'AthleteGroup']).isRequired,
      sourceGroupID: PropTypes.string,
      ageRange: PropTypes.string,
      gender: PropTypes.string,
      displayOrder: PropTypes.number,
      memberCount: PropTypes.number,
      events: PropTypes.arrayOf(
        PropTypes.shape({
          eventType: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.oneOf([null]), // Add null as valid type
          ]),
          location: PropTypes.string,
        })
      ),
    })
  ),
  clubEventTypes: PropTypes.arrayOf(
    PropTypes.shape({
      eventCode: PropTypes.number.isRequired,
      displayName: PropTypes.string.isRequired,
      category: PropTypes.string,
    })
  ),
  requirements: PropTypes.object,
  onRequirementChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
};

export default VolunteerRequirements;
